/**
 * Email-thread conversation geometry — the single source for the surface's
 * layout classes.
 *
 * The REAL components (conversation container, message card rows, day
 * divider) and the loading skeleton both read these constants, so the
 * skeleton cannot drift from the layout it imitates: a geometry change here
 * moves both in lockstep. Behavioral classes (pointer-events, hover tints,
 * relative positioning) stay at the call sites — only geometry lives here.
 *
 * The avatar in these rows is `<Avatar size='xs' />` (24px); the skeleton's
 * matching circle is AVATAR_XS_GEOMETRY.
 */

/**
 * The message card's header row — IDENTICAL collapsed and expanded so the
 * header never shifts on toggle. Only the sub-line swaps (snippet when
 * collapsed, recipients when expanded) and the body reveals below; a stable
 * header is what makes expand read as a reveal, not a jump.
 * Layout: avatar · [sender + sub-line] · timestamp · chevron.
 */
export const MESSAGE_HEADER_GEOMETRY =
  'flex items-center gap-2.5 px-3.5 py-2.5';

/** Padding around an expanded message's body block (below the header divider). */
export const EXPANDED_BODY_PADDING = 'px-3.5 pt-3 pb-4';

/** Matches `<Avatar size='xs' />` (24px circle) used in message rows. */
export const AVATAR_XS_GEOMETRY = 'size-6 rounded-full';

/**
 * A self-contained bordered rounded card surface used by the floating-card
 * redesign (one card per email message, stacked in date groups).
 */
export const CARD_GEOMETRY =
  'rounded-lg border border-[var(--color-border-secondary)] bg-[var(--color-bg-primary)] overflow-hidden';

/**
 * The date-group header strip (small date label + a hairline rule to its
 * right). The label styles and the `flex-1 border-t` rule live at the call
 * site.
 */
export const DATE_GROUP_HEADER_GEOMETRY = 'flex items-center gap-2 pt-1 pb-1';

/** Vertical spacing between date groups. */
export const DATE_GROUP_STACK_GEOMETRY = 'flex flex-col gap-4';

/** Vertical spacing between cards within a date group. */
export const CARD_STACK_GEOMETRY = 'flex flex-col gap-2';
